// Writing history kept by the app a project came from — Scrivener's Project ▸ Writing History (`ImportedHistory`,
// SuiteModel `ImportedHistory.swift`; `ScrivenerHistory`, SuiteImport). Scrivener records one net figure a day for the
// whole project, split into the Draft and elsewhere, so it's kept in the project settings (`settings.importedWords`, a
// map made on first use, so joined) as `"<day> <source> draft|other"` → net words. An import writes the days it read
// over the same keys (a zero is no key) and keeps the days it doesn't mention. The fragments' word logs record only
// typing here, so the two are added.
import type { DailyWords } from './contract.ts';
import { parseXML } from './import/xmlTree.ts';
import type { Writer } from './edit/typed.ts';
import { int } from './edit/typed.ts';

export interface ImportedDay {
  /** `YYYY-MM-DD`, the writer's calendar day as the other app recorded it. */
  day: string;
  /** The app it came from (`scrivener`). */
  source: string;
  /** Net words in the manuscript (Scrivener's Draft). */
  draft: number;
  /** Net words elsewhere: notes and research. */
  other: number;
}

export const IMPORTED_WORDS_KEY = 'importedWords';
export const SCRIVENER_HISTORY_SOURCE = 'scrivener';
/** Where a Scrivener package keeps its writing history. */
export const SCRIVENER_HISTORY_PATH = 'Files/writing.history';

/** `ImportedHistory.isDay`: a well-formed `yyyy-MM-dd`. */
export function isHistoryDay(s: string): boolean {
  return /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);
}

const byDayThenSource = (a: ImportedDay, b: ImportedDay) =>
  a.day < b.day ? -1 : a.day > b.day ? 1 : a.source < b.source ? -1 : a.source > b.source ? 1 : 0;

/** `importedHistory()`: the days in the project settings, by day then source. */
export function importedHistory(settings: Record<string, unknown>): ImportedDay[] {
  const map = settings[IMPORTED_WORDS_KEY];
  if (!map || typeof map !== 'object') return [];
  const days = new Map<string, ImportedDay>();
  for (const [key, value] of Object.entries(map as Record<string, unknown>)) {
    // `key.split(separator: " ")`: empty pieces omitted.
    const parts = key.split(' ').filter((p) => p !== '');
    if (parts.length !== 3 || !isHistoryDay(parts[0]) || (parts[2] !== 'draft' && parts[2] !== 'other')) continue;
    if (typeof value !== 'number' || !Number.isInteger(value)) continue;
    const id = `${parts[0]} ${parts[1]}`;
    const day = days.get(id) ?? { day: parts[0], source: parts[1], draft: 0, other: 0 };
    if (parts[2] === 'draft') day.draft = value; else day.other = value;
    days.set(id, day);
  }
  return [...days.values()].sort(byDayThenSource);
}

/** `Day.words(allText:)`: each net figure as added when it's a gain and removed when it's a loss. */
export function importedDayWords(d: ImportedDay, allText: boolean): { added: number; removed: number } {
  let added = 0, removed = 0;
  for (const n of allText ? [d.draft, d.other] : [d.draft]) { if (n > 0) added += n; else removed -= n; }
  return { added, removed };
}

/** `ImportedHistory.dailyWords(allText:)`, by day. */
export function importedDaily(days: ImportedDay[], allText: boolean): DailyWords[] {
  const out = new Map<string, DailyWords>();
  for (const d of days) {
    const w = importedDayWords(d, allText);
    if (w.added <= 0 && w.removed <= 0) continue;
    const day = out.get(d.day) ?? { day: d.day, added: 0, removed: 0 };
    day.added += w.added;
    day.removed += w.removed;
    out.set(d.day, day);
  }
  return [...out.values()];
}

/** Day lists added together, by day. */
export function addingDaily(a: DailyWords[], b: DailyWords[]): DailyWords[] {
  const out = new Map<string, DailyWords>();
  for (const w of [...a, ...b]) {
    const day = out.get(w.day) ?? { day: w.day, added: 0, removed: 0 };
    day.added += w.added;
    day.removed += w.removed;
    out.set(w.day, day);
  }
  return [...out.values()].sort((x, y) => (x.day < y.day ? -1 : x.day > y.day ? 1 : 0));
}

/** `ImportedHistory.name(of:)`. */
export function historySourceName(source: string): string {
  return source === SCRIVENER_HISTORY_SOURCE ? 'Scrivener' : source.slice(0, 1).toUpperCase() + source.slice(1);
}

/** `ScrivenerHistory.days`: the days in a `writing.history` file (`<Day dwc="…" owc="…">yyyy-MM-dd</Day>`). A day
 *  listed twice is added up; one that can't be read is passed by. */
export function scrivenerHistoryDays(bytes: Uint8Array): ImportedDay[] {
  const root = parseXML(bytes);
  const byDay = new Map<string, ImportedDay>();
  // Swift's `Int(_:)`: an optional sign and digits, nothing else.
  const integer = (s: string | undefined) => (s !== undefined && /^[+-]?[0-9]+$/.test(s) ? parseInt(s, 10) : undefined);
  for (const element of root.childrenNamed('Day')) {
    const day = element.text.trim();
    const draft = integer(element.attr('dwc')), other = integer(element.attr('owc'));
    if (!isHistoryDay(day) || (draft === undefined && other === undefined)) continue;
    const entry = byDay.get(day) ?? { day, source: SCRIVENER_HISTORY_SOURCE, draft: 0, other: 0 };
    entry.draft += draft ?? 0;
    entry.other += other ?? 0;
    byDay.set(day, entry);
  }
  return [...byDay.values()].sort(byDayThenSource);
}

/** `recordImportedHistory`: writes the days over what an earlier import recorded for them (a zero removes the key),
 *  keeping days not given; returns how many days were new or changed. */
export function recordImportedHistory(w: Writer, days: ImportedDay[]): number {
  const path = ['settings', IMPORTED_WORDS_KEY];
  const given = days.filter((d) => isHistoryDay(d.day) && d.source !== '' && !d.source.includes(' ')).sort(byDayThenSource);
  let changed = 0;
  for (const d of given) {
    let dayChanged = false;
    for (const [part, n] of [['draft', d.draft], ['other', d.other]] as const) {
      const key = `${d.day} ${d.source} ${part}`;
      const stored = w.stored(path, key);
      const storedInt = stored && (stored[0] === 'int' || stored[0] === 'uint') ? Number(stored[1]) : undefined;
      if (n === 0) {
        if (storedInt === undefined) continue;
        w.delete(path, key);
      } else {
        if (storedInt === n) continue;
        w.putIfChanged(path, key, int(n));
      }
      dayChanged = true;
    }
    if (dayChanged) changed++;
  }
  return changed;
}

/** `ScrivenerHistory.Summary`: what one reading brought in. */
export interface HistorySummary {
  days: number;
  changed: number;
  first: string | null;
  last: string | null;
  draftWords: number;
  otherWords: number;
}

export function historySummary(days: ImportedDay[], changed: number): HistorySummary {
  const sorted = days.map((d) => d.day).sort();
  return {
    days: days.length,
    changed,
    first: sorted[0] ?? null,
    last: sorted[sorted.length - 1] ?? null,
    draftWords: days.reduce((n, d) => n + d.draft, 0),
    otherWords: days.reduce((n, d) => n + d.other, 0),
  };
}
